import { db } from "@/lib/firebase";
import { formatUrlPath } from "@/lib/utils";
import {
  GA4InsightsGenerationJob,
  safeParseGA4InsightsGenerationJob,
} from "@/schemas";
import { GoogleAnalyticsInsight } from "@/types/GoogleAnalytics";
import {
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { StateCreator } from "zustand";
import { StoreState } from "..";

// Page Integration Types
export interface PageGoogleAnalyticsIntegration {
  insights: GoogleAnalyticsInsight[];
  isGeneratingInsights: boolean;
  lastInsightGeneration: Date | null;
  insightGenerationError: string | null;
  generationJob: GA4InsightsGenerationJob | null;
}

export interface PageIntegration {
  pageId: string;
  domainId: string;
  googleAnalytics: PageGoogleAnalyticsIntegration;
}

export interface PageIntegrationsSlice {
  /* state */
  pageIntegrations: Record<string, PageIntegration>; // keyed by pageId
  currentPageIntegrations: PageIntegration | null;
  isLoadingPageIntegrations: boolean;
  pageIntegrationsError: string | null;

  /* actions */
  subscribeToPageIntegrations: (
    orgId: string,
    domainId: string,
    pageId: string,
  ) => () => void;
  setCurrentPageIntegrations: (pageId: string | null) => void;
  generatePageInsights: (
    orgId: string,
    domainId: string,
    pageId: string,
  ) => Promise<void>;
  generateDomainPagesInsights: (
    orgId: string,
    domainId: string,
  ) => Promise<{
    triggered: number;
    failed: number;
  }>;
  setPageInsightGenerating: (pageId: string, isGenerating: boolean) => void;
  setPageInsightError: (pageId: string, error: string | null) => void;
  unsubscribePageIntegrations: () => void;

  /* generation job actions */
  subscribeToGenerationJob: (
    orgId: string,
    domainId: string,
    pageId: string,
  ) => () => void;
  setGenerationJob: (
    pageId: string,
    job: GA4InsightsGenerationJob | null,
  ) => void;

  /* internals */
  _pageIntegrationsUnsubscribe: (() => void) | null;
  _generationJobUnsubscribe: (() => void) | null;
}

const defaultPageGoogleAnalyticsIntegration: PageGoogleAnalyticsIntegration = {
  insights: [],
  isGeneratingInsights: false,
  lastInsightGeneration: null,
  insightGenerationError: null,
  generationJob: null,
};

// Timeout for stale generation jobs (10 minutes)
const GENERATION_JOB_TIMEOUT_MS = 10 * 60 * 1000;

const toDate = (timestamp: Date | { toDate?: () => Date }) => {
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp.toDate === "function") return timestamp.toDate();
  return new Date();
};

/**
 * Check if a generation job is stale (older than 10 minutes)
 * Used as fallback when backend never starts processing
 */
const isGenerationJobStale = (job: GA4InsightsGenerationJob): boolean => {
  if (job.status !== "pending") return false;
  if (!job.created_at) return false;

  const createdAt = toDate(job.created_at);
  const now = new Date();
  const ageMs = now.getTime() - createdAt.getTime();

  return ageMs > GENERATION_JOB_TIMEOUT_MS;
};

export const createPageIntegrationsSlice: StateCreator<
  StoreState,
  [],
  [],
  PageIntegrationsSlice
> = (set, get) => ({
  /* ---------- initial state ---------- */
  pageIntegrations: {},
  currentPageIntegrations: null,
  isLoadingPageIntegrations: false,
  pageIntegrationsError: null,
  _pageIntegrationsUnsubscribe: null,
  _generationJobUnsubscribe: null,

  /* ---------- actions ---------- */
  subscribeToPageIntegrations: (
    orgId: string,
    domainId: string,
    pageId: string,
  ) => {
    if (!orgId || !domainId || !pageId) {
      console.log(
        "Page integrations subscription requires orgId, domainId, and pageId.",
      );
      return () => {};
    }

    // Clean up any existing subscription
    get()._pageIntegrationsUnsubscribe?.();
    set({
      isLoadingPageIntegrations: true,
      pageIntegrationsError: null,
    });

    console.log(
      `Setting up page integrations listener for page ${pageId} in domain ${domainId}`,
    );

    // Subscribe to insights collection
    const insightsRef = collection(
      db,
      `organizations/${orgId}/domains/${domainId}/pages/${pageId}/integrations/google-analytics/insights`,
    );
    const insightsQuery = query(insightsRef, orderBy("created_at", "asc"));

    const unsubscribe = onSnapshot(
      insightsQuery,
      (snapshot) => {
        const insightsData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            // Document metadata
            insight_id: data.insight_id || doc.id,
            user_id: data.user_id || "",
            domain_id: data.domain_id || "",
            page_id: data.page_id || "",
            page_url: data.page_url || "",
            account_id: data.account_id || "",
            property_id: data.property_id || "",
            dimension: data.dimension || "",

            // Date range (top-level field)
            date_range: {
              start: data.date_range?.start || "",
              end: data.date_range?.end || "",
            },

            // Timestamps
            created_at: data.created_at?.toDate() || new Date(),
            updated_at: data.updated_at?.toDate() || new Date(),

            // Processing metadata
            processing_time_seconds: data.processing_time_seconds || 0,
            raw_data_cache_id: data.raw_data_cache_id || "",

            // Error field (present when insight generation fails)
            error: data.error,

            // Structured insights data
            structured_insights: data.structured_insights
              ? {
                  insights: data.structured_insights.insights || [],
                  total_insights: data.structured_insights.total_insights || 0,
                  total_input_tokens:
                    data.structured_insights.total_input_tokens || 0,
                  total_output_tokens:
                    data.structured_insights.total_output_tokens || 0,
                  total_tokens: data.structured_insights.total_tokens || 0,
                }
              : undefined,
          };
        }) as GoogleAnalyticsInsight[];

        console.log(`Insights snapshot for page ${pageId}:`, {
          insightsCount: insightsData.length,
        });

        // Get existing integration to check if we should reset generating state
        const existingIntegration = get().pageIntegrations[pageId];
        const previousInsightCount =
          existingIntegration?.googleAnalytics.insights.length || 0;
        const newInsightCount = insightsData.length;

        // Only reset isGeneratingInsights to false if we received new insights
        const hasNewInsights = newInsightCount > previousInsightCount;
        const shouldResetGenerating =
          hasNewInsights || (newInsightCount > 0 && !existingIntegration);

        const googleAnalytics: PageGoogleAnalyticsIntegration = {
          insights: insightsData,
          isGeneratingInsights: shouldResetGenerating
            ? false
            : existingIntegration?.googleAnalytics.isGeneratingInsights ||
              false,
          lastInsightGeneration:
            existingIntegration?.googleAnalytics.lastInsightGeneration || null,
          insightGenerationError: null,
          generationJob:
            existingIntegration?.googleAnalytics.generationJob || null,
        };

        const pageIntegration: PageIntegration = {
          pageId,
          domainId,
          googleAnalytics,
        };

        set({
          pageIntegrations: {
            ...get().pageIntegrations,
            [pageId]: pageIntegration,
          },
          currentPageIntegrations: pageIntegration,
          isLoadingPageIntegrations: false,
        });
      },
      (error) => {
        console.error(
          `Page integrations subscription error for ${pageId}:`,
          error,
        );
        set({
          pageIntegrationsError: `Failed to subscribe to page integrations: ${error.message}`,
          isLoadingPageIntegrations: false,
        });
      },
    );

    set({ _pageIntegrationsUnsubscribe: unsubscribe });

    // Check for an in-progress run among the latest generation job in the
    // run-keyed `jobs` collection (backend writes one doc per run, keyed by
    // generation_run_id). Handles page reload during generation.
    const jobsRef = collection(
      db,
      `organizations/${orgId}/domains/${domainId}/pages/${pageId}/integrations/google-analytics/jobs`,
    );
    const latestJobQuery = query(
      jobsRef,
      orderBy("created_at", "desc"),
      limit(1),
    );

    getDocs(latestJobQuery)
      .then((snapshot) => {
        const latest = snapshot.docs[0];
        if (!latest) return;

        const result = safeParseGA4InsightsGenerationJob({
          ...latest.data(),
          id: latest.id,
        });
        if (!result.success) return;
        if (result.data.status !== "pending") return;

        if (isGenerationJobStale(result.data)) {
          console.log(
            `Found stale generation job for page ${pageId}, ignoring...`,
          );
          return;
        }

        console.log(
          `Found pending generation job for page ${pageId}, subscribing...`,
        );
        get().setPageInsightGenerating(pageId, true);
        get().subscribeToGenerationJob(orgId, domainId, pageId);
      })
      .catch((error) => {
        console.error("Error checking for pending generation job:", error);
      });

    return unsubscribe;
  },

  setCurrentPageIntegrations: (pageId: string | null) => {
    if (!pageId) {
      set({ currentPageIntegrations: null });
      return;
    }

    const existingIntegration = get().pageIntegrations[pageId];
    if (existingIntegration) {
      set({ currentPageIntegrations: existingIntegration });
    } else {
      // Create default integration for new page
      const defaultIntegration: PageIntegration = {
        pageId,
        domainId: "", // Will be set by subscribeToPageIntegrations
        googleAnalytics: defaultPageGoogleAnalyticsIntegration,
      };
      set({
        currentPageIntegrations: defaultIntegration,
        pageIntegrations: {
          ...get().pageIntegrations,
          [pageId]: defaultIntegration,
        },
      });
    }
  },

  generatePageInsights: async (
    orgId: string,
    domainId: string,
    pageId: string,
  ) => {
    if (!orgId || !domainId || !pageId) {
      throw new Error("Organization ID, Domain ID, and Page ID are required");
    }

    const { GoogleAnalyticsService } =
      await import("@/lib/googleAnalyticsService");

    try {
      // Set loading state
      get().setPageInsightGenerating(pageId, true);
      get().setPageInsightError(pageId, null);

      // Kick off backend generation (backend creates the run-keyed job doc).
      const response = await GoogleAnalyticsService.generateInsights(
        domainId,
        pageId,
      );

      console.log("Page insights generation initiated:", response);

      // Subscribe to the latest run doc for real-time progress.
      get().subscribeToGenerationJob(orgId, domainId, pageId);

      // Update last generation timestamp
      set((state) => {
        const integration = state.pageIntegrations[pageId];
        if (integration) {
          const updatedIntegration = {
            ...integration,
            googleAnalytics: {
              ...integration.googleAnalytics,
              lastInsightGeneration: new Date(),
            },
          };

          return {
            ...state,
            pageIntegrations: {
              ...state.pageIntegrations,
              [pageId]: updatedIntegration,
            },
            currentPageIntegrations:
              state.currentPageIntegrations?.pageId === pageId
                ? updatedIntegration
                : state.currentPageIntegrations,
          };
        }
        return state;
      });
    } catch (error) {
      console.error("Failed to generate page insights:", error);
      get().setPageInsightError(
        pageId,
        error instanceof Error ? error.message : "Unknown error",
      );
      get().setPageInsightGenerating(pageId, false);
      throw error;
    }
  },

  generateDomainPagesInsights: async (orgId, domainId) => {
    const homepage = get().pages.find(
      (p) =>
        p.domainId === domainId &&
        !!p.url &&
        ["", "/"].includes(formatUrlPath(p.url)),
    );

    let triggered = 0;
    let failed = 0;

    if (homepage) {
      try {
        await get().generatePageInsights(orgId, domainId, homepage.id);
        triggered = 1;
      } catch {
        failed = 1;
      }
    }

    return { triggered, failed };
  },

  setPageInsightGenerating: (pageId: string, isGenerating: boolean) => {
    console.log(`Setting page insight generating for ${pageId}:`, isGenerating);
    set((state) => {
      const integration = state.pageIntegrations[pageId];
      if (!integration) return state;

      const updatedIntegration = {
        ...integration,
        googleAnalytics: {
          ...integration.googleAnalytics,
          isGeneratingInsights: isGenerating,
        },
      };

      return {
        ...state,
        pageIntegrations: {
          ...state.pageIntegrations,
          [pageId]: updatedIntegration,
        },
        currentPageIntegrations:
          state.currentPageIntegrations?.pageId === pageId
            ? updatedIntegration
            : state.currentPageIntegrations,
      };
    });
  },

  setPageInsightError: (pageId: string, error: string | null) => {
    set((state) => {
      const integration = state.pageIntegrations[pageId];
      if (!integration) return state;

      const updatedIntegration = {
        ...integration,
        googleAnalytics: {
          ...integration.googleAnalytics,
          insightGenerationError: error,
        },
      };

      return {
        ...state,
        pageIntegrations: {
          ...state.pageIntegrations,
          [pageId]: updatedIntegration,
        },
        currentPageIntegrations:
          state.currentPageIntegrations?.pageId === pageId
            ? updatedIntegration
            : state.currentPageIntegrations,
      };
    });
  },

  unsubscribePageIntegrations: () => {
    const unsubscribe = get()._pageIntegrationsUnsubscribe;
    if (unsubscribe) {
      console.log("Unsubscribing from page integrations listener.");
      unsubscribe();
      set({ _pageIntegrationsUnsubscribe: null });
    }

    // Also clean up generation job subscription
    const jobUnsubscribe = get()._generationJobUnsubscribe;
    if (jobUnsubscribe) {
      console.log("Unsubscribing from generation job listener.");
      jobUnsubscribe();
      set({ _generationJobUnsubscribe: null });
    }
  },

  /* ---------- generation job actions ---------- */
  subscribeToGenerationJob: (
    orgId: string,
    domainId: string,
    pageId: string,
  ) => {
    // Clean up any existing subscription
    get()._generationJobUnsubscribe?.();

    // Backend writes one doc per run, keyed by generation_run_id (UUID), into
    // the `jobs` collection. Track the latest run.
    const jobsRef = collection(
      db,
      `organizations/${orgId}/domains/${domainId}/pages/${pageId}/integrations/google-analytics/jobs`,
    );
    const latestJobQuery = query(
      jobsRef,
      orderBy("created_at", "desc"),
      limit(1),
    );

    console.log(`Subscribing to latest generation job for page ${pageId}`);

    const unsubscribe = onSnapshot(
      latestJobQuery,
      (snapshot) => {
        const latest = snapshot.docs[0];
        if (!latest) {
          get().setGenerationJob(pageId, null);
          return;
        }

        const result = safeParseGA4InsightsGenerationJob({
          ...latest.data(),
          id: latest.id,
        });
        if (!result.success) {
          console.error(
            "Failed to parse generation job:",
            result.error.issues,
          );
          return;
        }

        const jobData: GA4InsightsGenerationJob = result.data;

        // Check for stale job (older than 10 minutes and still pending)
        if (isGenerationJobStale(jobData)) {
          console.log(
            `Generation job for page ${pageId} is stale, treating as timed out`,
          );
          get().setGenerationJob(pageId, null);
          get().setPageInsightGenerating(pageId, false);

          // Unsubscribe from stale job
          setTimeout(() => {
            get()._generationJobUnsubscribe?.();
            set({ _generationJobUnsubscribe: null });
          }, 100);
          return;
        }

        console.log(`Generation job update for page ${pageId}:`, {
          status: jobData.status,
          completed: jobData.completed_dimensions,
          total: jobData.total_dimensions,
        });

        get().setGenerationJob(pageId, jobData);

        // When job completes or errors, reset generating state
        if (jobData.status === "completed" || jobData.status === "error") {
          get().setPageInsightGenerating(pageId, false);

          if (jobData.status === "error" && jobData.error_message) {
            get().setPageInsightError(pageId, jobData.error_message);
          }

          // Unsubscribe after completion
          setTimeout(() => {
            get()._generationJobUnsubscribe?.();
            set({ _generationJobUnsubscribe: null });
          }, 1000);
        }
      },
      (error) => {
        console.error(
          `Generation job subscription error for ${pageId}:`,
          error,
        );
      },
    );

    set({ _generationJobUnsubscribe: unsubscribe });
    return unsubscribe;
  },

  setGenerationJob: (pageId: string, job: GA4InsightsGenerationJob | null) => {
    set((state) => {
      const integration = state.pageIntegrations[pageId];
      if (!integration) return state;

      const updatedIntegration = {
        ...integration,
        googleAnalytics: {
          ...integration.googleAnalytics,
          generationJob: job,
        },
      };

      return {
        ...state,
        pageIntegrations: {
          ...state.pageIntegrations,
          [pageId]: updatedIntegration,
        },
        currentPageIntegrations:
          state.currentPageIntegrations?.pageId === pageId
            ? updatedIntegration
            : state.currentPageIntegrations,
      };
    });
  },
});
